/* ll-menu.js — de uitklappers bewegen zoals de bron het doet.
 *
 * Dit is UITGELEZEN uit de draaiende bron (`getAnimations()` tijdens het
 * openen), niet nagebouwd uit zijn broncode. Dat verschil kostte een ronde: uit
 * de code las ik 0,25s hoogte + 0,15s inhoud, maar op de winkel staat er dit:
 *
 *   OPENEN  300ms, alles tegelijk, `fill: both`
 *     hoogte    op de <details> zelf: dicht -> open op 0,8333 (=250ms), daarna
 *               vasthouden tot 300ms
 *     dekking   op de inhoud: 0 · 0 op 0,5 · 1 op 1
 *     verschuiving  translateY(4px) · 4px op 0,5 · 0 op 1
 *   SLUITEN 250ms
 *     hoogte    open -> dicht
 *     dekking   1 · 0 op 0,6 · 0 op 1
 *
 * DE HOOGTE ZIT OP DE <details>, NIET OP DE INHOUD. Dat is het hele punt. Zet
 * je hem op de inhoud, dan moeten de binnen- en buitenmarge mee en schuift de
 * tekst tijdens de beweging onder de kop door — dat is de hik die de eigenaar
 * zag bij het openen. Op de <details> blijft de inhoud gewoon staan en wordt
 * hij alleen afgeknipt.
 */
(() => {
  const OPEN_DUUR = 300;
  const DICHT_DUUR = 250;

  const magBewegen = () =>
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const inhoudVan = (details) =>
    Array.from(details.children).find((k) => k.tagName !== 'SUMMARY');

  /* DE VLAG MAG GEEN ATTRIBUUT ZIJN.
     Horizon haalt de kopsectie na het laden nog een keer op
     (`section-hydration.js`, via requestIdleCallback) en giet die verse HTML
     met `morph.js` OVER DEZELFDE ELEMENTEN heen. Het element blijft hetzelfde
     object, maar zijn attributen en kinderen worden vervangen door die van de
     server. Onze `data-ll-accordeon` verdween daarmee — terwijl de
     klikafhandelaar die eraan hing gewoon bleef zitten, want die zit op het
     object en niet op het attribuut.
     Gevolg: na de hydratie hing er een TWEEDE afhandelaar op dezelfde rij, en
     liep één klik door allebei de takken — eerst open, 1ms later weer dicht.
     De bezoeker zag dus niets gebeuren. Gemeten op 19 aug 2026 op de rij
     DRESSES: registratie 1 op t=896ms, registratie 2 op t=1196ms, hetzelfde
     summary-object, en het attribuut was er tussendoor afgehaald.
     Een WeakSet leeft in het geheugen en is dus immuun voor die morph. */
  const gehaakt = new WeakSet();

  function haakAan(details) {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    // De bovenste regels (MEN, WOMEN) openen een heel paneel en zijn dus geen
    // uitklapper — die laten we met rust.
    if (summary.hasAttribute('on:click') || summary.hasAttribute('command')) return;

    /* Dit attribuut stuurt niets meer aan, het is alleen om in de browser te
       kunnen ZIEN dat de rij behandeld is. Daarom staat het BOVEN de vlag: de
       hydratie veegt het weg, en dan zou een rij die gewoon werkt er in de
       inspector onbehandeld uitzien. Zo komt het elke ronde terug. */
    details.dataset.llAccordeon = '1';

    if (gehaakt.has(summary)) return;
    gehaakt.add(summary);

    summary.addEventListener('click', (gebeurtenis) => {
      /* Slot op de deur: mocht er ooit tóch een tweede afhandelaar aan dezelfde
         rij komen te hangen, dan zet één klik hem nog steeds maar één keer om.
         Het merk zit op de GEBEURTENIS, dus het geldt per klik. */
      if (gebeurtenis.llMenuGedaan) return;
      gebeurtenis.llMenuGedaan = true;

      const inhoud = inhoudVan(details);
      if (!inhoud || !magBewegen()) return;
      gebeurtenis.preventDefault();

      /* EERST METEN, DAN AFBREKEN. Klik je opnieuw terwijl hij nog beweegt, dan
         haalt `cancel()` de opgehouden eindstand weg en valt hij terug op zijn
         natuurlijke hoogte. Meet je daarná, dan begin je de terugweg vanaf de
         verkeerde plek: gemeten sprong van 133,4 naar 86 in één frame. */
      const nu = details.getBoundingClientRect().height;
      const dicht = summary.getBoundingClientRect().height;

      details.getAnimations().forEach((a) => a.cancel());
      inhoud.getAnimations().forEach((a) => a.cancel());

      const opruimen = (animaties, dan) => {
        Promise.allSettled(animaties.map((a) => a.finished)).then(() => {
          if (dan) dan();
          animaties.forEach((a) => a.cancel());
          details.style.overflow = '';
          details.style.height = '';
        });
      };

      if (details.open) {
        /* Het plusje volgt een eigen vlaggetje en niet het `open`-kenmerk.
           Reden: `details.open` gaat bij het SLUITEN pas op het eind uit, en dan
           zou het plusje 250ms te laat terugdraaien. Bij de bron draait hij
           meteen mee. */
        details.removeAttribute('data-ll-open');
        details.style.overflow = 'hidden';
        const krimp = details.animate(
          [
            { height: nu + 'px', easing: 'ease' },
            { height: dicht + 'px', easing: 'ease' },
          ],
          { duration: DICHT_DUUR, easing: 'linear', fill: 'both' }
        );
        const vervaag = inhoud.animate(
          [
            { opacity: 1, easing: 'ease' },
            { opacity: 0, offset: 0.6, easing: 'ease' },
            { opacity: 0, easing: 'ease' },
          ],
          { duration: DICHT_DUUR, easing: 'linear', fill: 'both' }
        );
        /* EERST dichtzetten, DAN de animaties loslaten. Andersom valt de
           opgehouden eindstand weg terwijl de inhoud nog in beeld staat, en zie
           je hem één frame op volle hoogte terugknallen. */
        opruimen([krimp, vervaag], () => {
          details.open = false;
        });
      } else {
        /* Even echt openzetten om de VOLLE hoogte te meten, en dan pas
           terugklemmen naar waar hij nu staat — allemaal binnen dezelfde tik,
           dus de browser tekent er niets van. */
        details.open = true;
        details.setAttribute('data-ll-open', '');
        details.style.overflow = '';
        details.style.height = '';
        const vol = details.getBoundingClientRect().height;
        if (!vol || vol <= dicht) return;   // niets te animeren

        details.style.overflow = 'hidden';
        const groei = details.animate(
          [
            { height: nu + 'px', easing: 'ease' },
            { height: vol + 'px', offset: 0.8333333333333334, easing: 'ease' },
            { height: vol + 'px', easing: 'ease' },
          ],
          { duration: OPEN_DUUR, easing: 'linear', fill: 'both' }
        );
        /* De versnelling zit op de KEYFRAMES en de tijdlijn zelf loopt
           `linear` — precies zoals de bron. Zet je `ease` op de tijdlijn, dan
           schuift het punt 0,5 naar voren en begint de tekst al na 100ms te
           verschijnen in plaats van na 150. Gemeten verschil: vier frames. */
        const verschijn = inhoud.animate(
          [
            { opacity: 0, easing: 'linear' },
            { opacity: 0, offset: 0.5, easing: 'ease' },
            { opacity: 1, easing: 'ease' },
          ],
          { duration: OPEN_DUUR, easing: 'linear', fill: 'both' }
        );
        const schuif = inhoud.animate(
          [
            { transform: 'translateY(4px)', easing: 'linear' },
            { transform: 'translateY(4px)', offset: 0.5, easing: 'ease' },
            { transform: 'translateY(0px)', easing: 'ease' },
          ],
          { duration: OPEN_DUUR, easing: 'linear', fill: 'both' }
        );
        opruimen([groei, verschijn, schuif]);
      }
    });
  }

  function scan(wortel) {
    (wortel || document)
      .querySelectorAll('accordion-custom > details, .menu-drawer details')
      .forEach(haakAan);
  }

  /* Horizon vouwt navigatie-items die niet passen op in een knop "More". Die
   * willen we nooit zien. Het element heeft daar een schakelaar voor, maar die
   * mag je NIET meteen zetten: doe je dat terwijl het onderdeel zichzelf nog
   * aan het opbouwen is, dan valt de hele kop terug op de mobiele indeling —
   * hamburger, gecentreerd logo, twee zoekknoppen — en herstelt hij niet meer.
   * Gereproduceerd in 1 van de 4 laadbeurten. Daarom pas nadat de pagina en de
   * lettertypes klaar zijn, want dan heeft Horizon zijn eigen meting al gedaan. */
  /* Hier stond een geenMore(): die zette `disabled="true"` op <overflow-list>
     om Horizons knop "More" weg te krijgen. Dat is precies wat mijn eigen
     aantekening in ll-custom.css verbiedt, en het beet weer: op een breed
     scherm verscheen af en toe de mobiele kop — hamburger, logo in het midden,
     twee zoekknoppen. Met dat kenmerk erop herstelt Horizon niet meer.
     De knop "More" blijft weg via `flex-wrap: wrap` in ll-custom.css: dan is
     er nooit overloop, dus komt die knop er ook nooit. */

  /* Kijken naar `addedNodes` is NIET genoeg, precies zoals verderop bij de
     klikbare takken al bleek: Horizon VERPLAATST de uitklappers van de lade in
     plaats van ze toe te voegen, en dan komt er niets binnen dat die summaries
     als afstammeling heeft. Gemeten op 19 aug: geen enkele uitklapper in de
     lade droeg `data-ll-accordeon`, dus die openden met Horizons eigen
     instant-gedrag in plaats van de beweging van de bron.

     Daarom bij elke wijziging de hele pagina opnieuw langs. Dat mag: de eerste
     regel van `haakAan` slaat alles over wat de haak al heeft. */
  scan();
  new MutationObserver(() => scan()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  window.addEventListener('load', () => scan());
  document.addEventListener('click', () => setTimeout(() => scan(), 60), true);

  /* ── De takken van de boom klikbaar maken ──────────────────────────────
     Horizon rendert een tak met onderliggende items als <summary>: die opent
     alleen maar. Op niveau 1 staat het adres nog in het paneel eronder, op
     niveau 2 nergens — terwijl "Footwear" gewoon een collectie is.

     De adressen komen uit ll-menu-links.liquid als JSON. Hier krijgt de TEKST
     van de tak een link; de rest van de rij en het plusje blijven openen. Dat
     is bewust: één rij die zowel opent als navigeert doet altijd het verkeerde.

     Let op de volgorde in de klikafhandeling: een klik in een <summary> opent
     de <details> als STANDAARDGEDRAG. `stopPropagation` helpt daar niet tegen;
     het moet `preventDefault` zijn, en daarna zelf navigeren. */
  const KAART = (() => {
    const el = document.getElementById('ll-menu-links');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  })();

  function padVan(samenvatting) {
    /* Eerst had ik hier `n.parentElement.closest('details')`, en dat gaf de
       EIGEN <details> terug — de lus liep dan op zichzelf vast en maakte
       "Men > Men > Men". Je moet eerst uit de eigen details stappen. */
    const stukken = [];
    let sam = samenvatting;
    while (sam) {
      const t = sam.querySelector('.menu-drawer__menu-item-text');
      if (t) stukken.unshift(t.textContent.trim());
      const eigen = sam.parentElement;                       // <details>
      const boven = eigen?.parentElement?.closest('details'); // de details erboven
      sam = boven ? boven.querySelector(':scope > summary') : null;
      if (stukken.length > 5) break;
    }
    return stukken.join(' > ');
  }

  /* Zelfde verhaal als hierboven, en hier zijn de twee helften ONGELIJK: de
     <a> die wij in de rij zetten wordt door de hydratie weggegooid en moet dus
     telkens terug, terwijl de klikafhandelaar juist blijft zitten en er dus
     maar één keer bij mag. Stonden ze onder één vlag, dan kreeg elke rij er na
     de hydratie een tweede afhandelaar bij. */
  const takGehaakt = new WeakSet();

  function maakTakkenKlikbaar(wortel) {
    (wortel || document).querySelectorAll('summary.menu-drawer__menu-item').forEach((sam) => {
      const titel = sam.querySelector('.menu-drawer__menu-item-text');
      if (!titel) return;

      const url = KAART[padVan(sam)];
      if (!url) return;

      if (!titel.querySelector('a')) {
        sam.dataset.llLink = url;
        const a = document.createElement('a');
        a.className = 'll-tak-link';
        a.href = url;
        while (titel.firstChild) a.appendChild(titel.firstChild);
        titel.appendChild(a);
      }

      if (takGehaakt.has(sam)) return;
      takGehaakt.add(sam);

      sam.addEventListener('click', (e) => {
        const link = e.target.closest?.('.ll-tak-link');
        if (!link) return;          // ergens anders op de rij: gewoon openen
        e.preventDefault();          // anders klapt de tak ook nog open
        window.location.href = link.getAttribute('href');
      });
    });
  }

  /* Op het moment dat dit script draait staan er nog maar twee uitklappers in
     de pagina; de elf van de lade komen er later bij. Kijken naar `addedNodes`
     was niet genoeg — Horizon VERPLAATST ze, en dan komt er niets nieuws
     binnen dat de summaries als afstammeling heeft. Gemeten: 0 van de 11
     kregen een link, terwijl dezelfde code met de hand uitgevoerd er 11 deed.

     Daarom bij elke wijziging gewoon de hele pagina opnieuw langs. Dat mag,
     want de eerste regel van de functie slaat alles over wat al een link
     heeft. */
  /* ── De kop mag niet wegspringen als er een lade opengaat ──────────────
     Klacht: sta je diep op een pagina en open je het menu, dan springt de kop
     bij het sluiten lelijk terug op zijn plek. Bij de bron gebeurt dat niet.

     Gemeten op 390px, halverwege een collectiepagina (scroll 900):

       kop vóór het openen   y = -1     (hij plakt bovenaan)
       kop mét het menu open y = -848,9 (hij valt terug naar zijn plek in de tekst)
       kop na het sluiten    y = -1

     De inhoud van de pagina bleef al die tijd stilstaan (het eerste kaartje op
     y -181). Het is dus alleen de KOP die heen en weer schiet.

     Waarom: de kop plakt met `position: sticky` aan het VENSTER, want onder de
     990px schuift bij Horizon het venster (zijn `.page-wrapper` wordt pas een
     schuifvak vanaf 990px — dat staat in base.css). Gaat er een lade open, dan
     zet Horizon `scroll-lock` op `<html>`, en dat betekent
     `html[scroll-lock] { overflow: hidden }`. Daarmee is het venster geen
     schuifvak meer, heeft de kop niets meer om aan te plakken, en valt hij terug
     naar zijn eigen plek in de tekst — 848 pixels hoger. De bron heeft dit niet,
     want die schuift op elke breedte een binnenvak.

     De reparatie: zolang het slot erop zit zetten we de kop op `fixed` op exact
     de plek waar hij toch al plakte (-1px), en geven we `.page-wrapper` de
     hoogte van de kop terug als bovenmarge, zodat de rest van de pagina niet
     omhoog schuift nu de kop uit de tekststroom valt.

     ALLEEN als hij ook echt plakt. Sta je bovenaan de pagina, dan staat de kop
     onder de aankondigingsbalk op y 51 en is er niets aan de hand; hem dan
     vastzetten zou hem 52 pixels omhoog trekken. Vandaar de toets `s < -1`. */
  (() => {
    const html = document.documentElement;
    const zet = () => {
      const kop = document.querySelector('header-component.header')?.closest('header.shopify-section');
      if (!kop) return;
      if (html.hasAttribute('scroll-lock')) {
        if (html.hasAttribute('ll-kop-vast')) return;      // al vast: niet opnieuw meten
        if (kop.getBoundingClientRect().top < -1) html.setAttribute('ll-kop-vast', '');
      } else {
        html.removeAttribute('ll-kop-vast');
      }
    };
    new MutationObserver(zet).observe(html, { attributes: true, attributeFilter: ['scroll-lock'] });
    zet();
  })();

  const opnieuw = () => maakTakkenKlikbaar();
  opnieuw();
  new MutationObserver(opnieuw).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', opnieuw);
  document.addEventListener('click', () => setTimeout(opnieuw, 60), true);

})();
